import React, { useState, useEffect, ReactNode } from "react";
import SettingList from "./SettingList";
import { useStorefrontSettings } from "./StorefrontSettingsProvider";
import { TabConfig, TabbedSettings } from "./TabbedSettings";
import { Modal, Spinner } from "../../components";
import { Button } from "../../components/primitives";
import { FormInput } from "../../components/form";
import { Alert, Badge, Input, Label, Note } from "../../components/primitives";
import { RadioRows, type RadioRowOption } from "../../components/RadioRows";
import { useMutation, useQuery } from '@apollo/client';
import { DeletePayoutAccountDocument, GetPayoutOptionsDocument, GetSellerDocument } from '../../gql/graphql';
import { useNavigate } from "react-router-dom";
import { requirementsMessage } from "./stripeRequirements";

/**
 * What the seller gets paid into, and — quietly — who moves the money.
 *
 * The spike names the row "Bank account" with a small "via Stripe" chip beside
 * it. The rows here previously showed the provider's logo as the whole label,
 * so the heading text existed nowhere on screen; my first fix put the logo
 * *and* a name, which was still not what is drawn. The seller is choosing
 * where money lands, not which vendor processes it.
 */
const ProviderLabel: React.FC<{ name: string; via?: string }> = ({ name, via }) => (
    <span className="flex items-center gap-2">
        <span>{name}</span>
        {via && <Badge variant="neutral">via {via}</Badge>}
    </span>
);

/**
 * One stored detail of a connected account.
 *
 * These were rendered as bordered, grey-filled boxes with a label above each —
 * three read-only values dressed as disabled text inputs, which invited a
 * click and did nothing.
 */
const AccountFact: React.FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-right text-sm font-medium text-foreground">{children}</span>
    </div>
);

const PayoutAccount: React.FC = () => {
    const { seller } = useStorefrontSettings();
    const { payoutAccount } = seller || {};
    const navigate = useNavigate();
    
    // State for delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Determine the selected provider based on the payoutAccount type
    const [selectedProvider, setSelectedProvider] = useState<string | null>(
        payoutAccount?.__typename === 'StripePayoutAccount' ? 'stripe' : 
        payoutAccount?.__typename === 'PaypalPayoutAccount' ? 'paypal' : null
    );

    // The accordion's separate open/closed state is gone: RadioRows discloses
    // the selected row's content, so "which one is open" and "which one is
    // chosen" were two names for the same fact, kept in sync by hand.

    // Get available payout options
    const { data: payoutOptionsData, loading: loadingPayoutOptions } = useQuery(GetPayoutOptionsDocument, {
        fetchPolicy: "network-only"
    });
    const payoutOptions = payoutOptionsData?.payoutOptions;
    const hasPayoutOptions = payoutOptions && (payoutOptions.stripe || payoutOptions.paypal);
    
    // Check if only one option is available
    const onlyOneOption = 
        (payoutOptions?.stripe && !payoutOptions?.paypal) || 
        (!payoutOptions?.stripe && payoutOptions?.paypal);

    /*
      Land on a choice rather than on nothing.

      With one option available it was already selected; with two, the screen
      opened with neither row chosen and no configuration showing, so a seller
      setting up payouts for the first time met a question with no answer in
      front of them. The design preselects the bank transfer — it is the one
      most sellers want, and every row still discloses its own setup the moment
      it is chosen, so preselecting costs nothing but a click saved.

      Only when nothing is connected yet: once there is an account, the
      selection reflects what the seller actually has.
    */
    useEffect(() => {
        if (payoutOptions && !payoutAccount && !selectedProvider) {
            if (payoutOptions.stripe) {
                setSelectedProvider('stripe');
            } else if (payoutOptions.paypal) {
                setSelectedProvider('paypal');
            }
        }
    }, [payoutOptions, payoutAccount, selectedProvider]);

    // Mutation to delete payout account
    const [deletePayoutAccount, { loading: loadingDelete }] = useMutation(DeletePayoutAccountDocument, {
        onCompleted: () => {
            setShowDeleteModal(false);
            setSelectedProvider(null);
        },
        refetchQueries: [{ query: GetSellerDocument }]
    });

    const handleStripeConnect = () => {
        navigate('/stripe-redirect/ACCOUNT_ONBOARDING');
    };

    const handlePaypalConnect = () => {
        navigate('/paypal-redirect');
    };
    
    const handleDeleteAccount = () => {
        setShowDeleteModal(true);
    };
    
    const confirmDeleteAccount = () => {
        deletePayoutAccount();
    };

    const isLoading = loadingPayoutOptions || loadingDelete;

    /**
     * The account that is already set up: what it is, where the money goes,
     * and the two things you can do about it.
     */
    const renderConnectedAccount = () => {
        const isStripe = payoutAccount?.__typename === 'StripePayoutAccount';
        const isPaypal = payoutAccount?.__typename === 'PaypalPayoutAccount';
        if (!isStripe && !isPaypal) return null;

        return (
            <>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Connected</h3>
            <div className="rounded-lg border border-border">
                <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                    {/* Named the same way as the row you picked it from, and
                        the way the seller thinks of it: it is their bank
                        account; Stripe is how it gets there. */}
                    <ProviderLabel
                        name={isStripe ? 'Bank account' : 'PayPal'}
                        via={isStripe ? 'Stripe' : undefined}
                    />
                    <Badge variant="success" dot>
                        Connected
                    </Badge>
                </div>

                <div className="px-4 py-3">
                    {/* The stored details sit on the sunken surface, as drawn —
                        they are a record, not a set of fields. */}
                    <div className="rounded-md bg-muted/60 px-3.5 py-1.5">
                        {isStripe && payoutAccount.bankAccount && (
                            <>
                                <AccountFact label="Bank">{payoutAccount.bankAccount.name}</AccountFact>
                                <AccountFact label="Account">
                                    <span className="font-mono">
                                        ••••{payoutAccount.bankAccount.last4Digits}
                                    </span>
                                </AccountFact>
                                <AccountFact label="Currency">
                                    {payoutAccount.bankAccount.currency?.toUpperCase()}
                                </AccountFact>
                            </>
                        )}

                        {isPaypal && (
                            <AccountFact label="PayPal account">{payoutAccount.email}</AccountFact>
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-2">
                        {/* `color` is not a Button prop — it was being passed
                            straight through to the DOM, so every Remove in the
                            app rendered in the ordinary text colour. */}
                        <Button
                            variant="ghost"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={handleDeleteAccount}
                            disabled={isLoading}
                        >
                            Remove
                        </Button>
                        {/* Outline, not filled: the page's primary action is
                            Save on the floating bar, and a filled Modify inside
                            a card competed with it. */}
                        <Button
                            variant="outline"
                            onClick={
                                isStripe
                                    ? () => navigate('/stripe-redirect/ACCOUNT_UPDATE')
                                    : handlePaypalConnect
                            }
                            disabled={isLoading}
                        >
                            Modify
                        </Button>
                    </div>
                </div>
            </div>
            </>
        );
    };

    /**
     * Stripe holding payouts, as its own card under the account rather than a
     * note inside it — the account itself is fine, and this is a separate
     * thing that needs doing. The spike draws it as a card with a tinted icon
     * chip and a link into the flow that clears it.
     */
    const renderRequirementsDue = () => {
        if (payoutAccount?.__typename !== 'StripePayoutAccount' || !payoutAccount.requirementsDue) {
            return null;
        }
        // What Stripe is actually waiting for, where it names something we can
        // put into words; the generic pair otherwise.
        const message = requirementsMessage(payoutAccount.outstandingRequirements ?? []);
        return (
            // The shared message component. This was a hand-drawn copy of it —
            // its own border, its own amber chip with a "!" in it — so it
            // missed the tint every other warning in the app now carries, and
            // a payout being held read quieter than a saved setting.
            <Alert variant="warning" title={message.title} className="mt-3" action={
                <button
                    type="button"
                    onClick={() => navigate('/stripe-redirect/ACCOUNT_UPDATE')}
                    className="text-xs font-medium text-primary hover:underline"
                >
                    {'Finish setup →'}
                </button>
            }>
                {message.body}
            </Alert>
        );
    };

    /**
     * Choosing a provider, in the same quiet-rows pattern the shipping section
     * uses — the selected row discloses its connect button. This was a stack
     * of bare radios and logos in a hand-rolled accordion that behaved like a
     * radio group without being one.
     */
    const renderAccountSelection = () => {
        if (payoutAccount) {
            return (
                <>
                    {renderConnectedAccount()}
                    {renderRequirementsDue()}
                </>
            );
        }

        if (!hasPayoutOptions && !loadingPayoutOptions) {
            return (
                <Note tone="critical" title="No payout options available">
                    The shop hasn&apos;t enabled a way to pay sellers yet. Contact the marketplace
                    to get set up.
                </Note>
            );
        }

        const options: RadioRowOption<string>[] = [];
        if (payoutOptions?.stripe) {
            options.push({
                value: 'stripe',
                label: <ProviderLabel name="Bank account" via="Stripe" />,
                description: 'Direct to your bank. Settles in 1–2 business days. No transfer fee.',
                testId: 'payout-option-stripe',
                content: (
                    <Button onClick={handleStripeConnect} loading={isLoading}>
                        Connect bank account
                    </Button>
                ),
            });
        }
        if (payoutOptions?.paypal) {
            options.push({
                value: 'paypal',
                label: <ProviderLabel name="PayPal" />,
                description: "Instant to your PayPal balance. PayPal's receiving fees apply.",
                testId: 'payout-option-paypal',
                content: (
                    <Button onClick={handlePaypalConnect} disabled={isLoading}>
                        Authorise PayPal
                    </Button>
                ),
            });
        }

        // With one provider there is no choice to make, so there is no radio to
        // make it with — just the provider and the button that connects it.
        if (onlyOneOption && options.length === 1) {
            const only = options[0];
            return (
                <div className="rounded-lg border border-border px-4 py-3.5" data-testid={only.testId}>
                    <div className="text-sm font-medium text-foreground">{only.label}</div>
                    <p className="mt-1 text-xs text-muted-foreground">{only.description}</p>
                    <div className="mt-3.5">{only.content}</div>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Choose a payout method</h3>
                <p className="text-sm text-muted-foreground">
                    Choose how you&apos;d like to be paid. You can change this later.
                </p>
                <RadioRows
                    name="payout-provider"
                    aria-label="Payout method"
                    value={selectedProvider ?? undefined}
                    onChange={setSelectedProvider}
                    options={options}
                />
                {/* Says the cost of changing your mind, before you choose. */}
                <p className="text-xs text-muted-foreground">
                    You can switch methods any time; pending payouts finish on the old method.
                </p>
            </div>
        );
    };

    return (
        <>
            <SettingList.Section
                title="Payout account"
                description="Where your earnings are sent when a payout settles."
                showFooterActions={false}
            >
                <div className="col-span-6">
                    {loadingPayoutOptions ? (
                        <div className="flex justify-center py-6">
                            <Spinner size="small" />
                        </div>
                    ) : (
                        renderAccountSelection()
                    )}
                </div>
            </SettingList.Section>
            
            {/* Delete Confirmation Modal */}
            <Modal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Remove payout account"
            >
                <div className="p-6">
                    <p className="mb-4 text-foreground">
                        Are you sure you want to remove your payout account? This action cannot be undone.
                    </p>
                    <p className="mb-6 text-sm text-muted-foreground">
                        You will need to set up a new payout account to receive future payments.
                    </p>
                    <div className="flex justify-end gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => setShowDeleteModal(false)}
                            disabled={loadingDelete}
                        >
                            Cancel
                        </Button>
                        {/* The confirm button in a destructive dialog is the
                            destructive variant — it was `variant="primary"`
                            with a `color` prop that does nothing, so the
                            button confirming an irreversible removal looked
                            exactly like Save. */}
                        <Button
                            variant="destructive"
                            onClick={confirmDeleteAccount}
                            loading={loadingDelete}
                        >
                            Remove
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

const Company: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            title="Business"
            description="Who you are as a legal entity. Never shown to customers."
            // Last section on its page either way, and the spike draws no rule
            // under the last one.
            showFooterActions={false}>
            <div className="col-span-6">
                <FormInput
                    name={`companyName`}
                    autoComplete="off"
                    type="text"
                    label="Company name"
                    // The app asks for three names and none of the three said
                    // which it was — this one, a store name and a store title,
                    // with nothing distinguishing them but their labels. Each
                    // now says what it is and shows the kind of thing it wants.
                    help="Your registered business name, as it appears on invoices and payout statements — “JJL Limited”."
                    disabled={settings.updating}
                />
            </div>
        </SettingList.Section>
    );
};

const Personal: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            title="Personal details"
            description="Only the marketplace team sees these. Customers see your store name.">
            <div className="col-span-3">
                <FormInput
                    name={`firstName`}
                    autoComplete="off"
                    type="text"
                    label="First name"
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-3">
                <FormInput
                    name={`lastName`}
                    autoComplete="off"
                    type="text"
                    label="Last name"
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-6">
                <FormInput
                    name={`phone`}
                    autoComplete="off"
                    type="text"
                    label="Phone"
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-6">
                <div className="space-y-1.5">
                    <Label htmlFor="profile-email">Email</Label>
                    <Input
                        id="profile-email"
                        autoComplete="off"
                        type="text"
                        value={settings.seller?.email ?? ''}
                        disabled={true}
                    />
                    {/* The field is disabled and the spike says why, rather
                        than leaving a dead box with no explanation. */}
                    <p className="text-xs text-muted-foreground">
                        Your sign-in. Contact the shop to change it.
                    </p>
                </div>
            </div>
        </SettingList.Section>
    );
};

/**
 * The Profile destination carries both sections, as the spike draws it —
 * personal details and the business name are one page about who you are.
 */
const PersonalAndCompany: React.FC = () => (
    <>
        <Personal />
        <Company />
    </>
);

const PayoutAccountTab: React.FC = () => {
    return <PayoutAccount />;
};

const tabConfig: TabConfig = {
    // Sentence case, like every other destination in the settings rail.
    personal: {
        title: "Profile",
        component: PersonalAndCompany,
        path: "profile",
        // Both sections are on the page, so both sets of fields have to be in
        // the form — otherwise Company name renders detached from its value.
        config: { personal: true, company: true },
    },
    company: {
        title: "Business",
        component: Company,
        path: "company",
        config: { company: true },
    },
    payout: {
        title: "Payouts",
        component: PayoutAccountTab,
        path: "payout",
        config: { payout: true },
        showFooterActions: false
    },
};

export const ProfileSettings: React.FC = () => {
    return <TabbedSettings title={"Settings"} tabConfig={tabConfig} />;
};

